import axios from 'axios';
import { useEffect, useState, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  baseURL,
  getMeasurements,
  getQuarterlyComparisonReport,
} from '../../../../helpers/api';
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
  TextField,
} from '@mui/material';
import {
  FilterList,
  VisibilityOutlined,
  PictureAsPdfOutlined,
} from '@mui/icons-material';
import { getYears } from '../../../../helpers/dateHelpers';



export default function QuarterlyComparisonReport({ props }) {
  const { userId } = props || {};
  const theme = useTheme();
  const iframeRef = useRef(null);

  const initFacility = { facilityId: '', facilityName: '' };
  const [facilityList, setFacilityList] = useState([]);
  const [facilityLoading, setFacilityLoading] = useState(false);
  const [facilitySelected, setFacilitySelected] = useState(initFacility);
  const [yearSelected, setYearSelected] = useState(new Date().getFullYear());

  const [measurementLoading, setMeasurementLoading] = useState(false);
  const [measurementData, setMeasurementData] = useState([]);
  const [measurementSelected, setMeasurementSelected] = useState('');

  const [htmlReport, setHtmlReport] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    fetchFacility();
    fetchMeasurement();
  }, []);

  const fetchFacility = async () => {
    setFacilityLoading(true);
    try {
      const url = `${baseURL}/TelerikReports/GetFacilityList/${userId}`;
      const res = await axios.get(url);
      if (res?.status === 200) {
        setFacilityList(res.data);
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
    } finally {
      setFacilityLoading(false);
    }
  };

  const fetchMeasurement = () => {
    setMeasurementLoading(true);
    getMeasurements()
      .then((response) => {
        setMeasurementData(response?.result?.data || []);
      })
      .catch((err) => console.error('Failed to fetch measurements', err))
      .finally(() => setMeasurementLoading(false));
  };

  const handlePreviewReport = async () => {
    setReportLoading(true);
    try {
      const res = await getQuarterlyComparisonReport(
        facilitySelected?.facilityId,
        yearSelected,
        measurementSelected
      );
      setHtmlReport(res?.result?.data || res?.data || '');
    } catch (error) {
      console.error('Error fetching HTML report:', error);
    } finally {
      setReportLoading(false);
    }
  };

  // Direct programmatic PDF conversion and download using html2canvas & jsPDF
  const handleDownloadPdf = async () => {
    const iframeBody = iframeRef.current?.contentDocument?.body;
    if (!iframeBody) return;

    setDownloadingPdf(true);
    try {
      const canvas = await html2canvas(iframeBody, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        windowWidth: iframeBody.scrollWidth,
        windowHeight: iframeBody.scrollHeight,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add remaining pages if content overflows A4 height
      while (heightLeft > 0) {
        position -= pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(
        `${facilitySelected?.facilityName || 'Quarterly'}_Comparison_Report_${yearSelected}.pdf`
      );
    } catch (error) {
      console.error('Error generating PDF download:', error);
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <Box sx={{ height: '90vh', bgcolor: 'background.default', overflow: 'hidden' }}>
      <Grid2 container sx={{ height: '100%' }} spacing={0}>
        {/* Main Preview Area */}
        <Grid2
          size={{ lg: 9, md: 8, sm: 12, xs: 12 }}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          {/* Header Bar */}
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
              Report Preview
            </Typography>

            {htmlReport && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={
                  downloadingPdf ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PictureAsPdfOutlined />
                  )
                }
                disabled={downloadingPdf}
                onClick={handleDownloadPdf}
                sx={{ textTransform: 'none', fontSize: '0.8rem' }}
              >
                {downloadingPdf ? 'Downloading PDF...' : 'Download PDF'}
              </Button>
            )}
          </Box>

          {/* Embedded Frame */}
          <Box
            sx={{
              flexGrow: 1,
              borderRight: 1,
              borderColor: 'divider',
              bgcolor: '#f8f9fa',
              position: 'relative',
            }}
          >
            {reportLoading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading HTML report...
                </Typography>
              </Stack>
            ) : htmlReport ? (
              <iframe
                ref={iframeRef}
                srcDoc={htmlReport}
                title="Report Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  backgroundColor: '#fff',
                }}
              />
            ) : (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ height: '100%', color: 'text.secondary' }}
              >
                <Typography variant="h6">No Report Loaded</Typography>
                <Typography variant="body2">
                  Select Facility, Year, and Measurement, then click "Preview".
                </Typography>
              </Stack>
            )}
          </Box>
        </Grid2>

        {/* Right Sidebar Controls */}
        <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }} sx={{ height: '100%', bgcolor: 'white' }}>
          <Stack sx={{ height: '100%' }}>
            <Box
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterList color="primary" fontSize="small" />
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="primary.main"
                  sx={{ textTransform: 'uppercase' }}
                >
                  Filter Controls
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
              <Stack spacing={3}>
                <Autocomplete
                  fullWidth
                  options={facilityList}
                  getOptionLabel={(option) => option.facilityName || ''}
                  value={facilitySelected}
                  onChange={(_, value) => value && setFacilitySelected(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Facility"
                      size="small"
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: facilityLoading && (
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                        ),
                      }}
                    />
                  )}
                />

                <FormControl fullWidth size="small">
                  <InputLabel>Year *</InputLabel>
                  <Select
                    label="Year *"
                    size="small"
                    value={yearSelected}
                    onChange={(e) => setYearSelected(e.target.value)}
                  >
                    {getYears().map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Measurement *</InputLabel>
                  <Select
                    size="small"
                    label="Measurement *"
                    value={measurementSelected}
                    onChange={(e) => setMeasurementSelected(e.target.value)}
                    required
                    startAdornment={
                      measurementLoading && <CircularProgress size={20} sx={{ mr: 1 }} />
                    }
                  >
                    {measurementData?.map((m) => (
                      <MenuItem key={m?.measurementId} value={m?.measurementId}>
                        {m?.measurementName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            <Divider />

            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={
                  reportLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <VisibilityOutlined />
                  )
                }
                disabled={reportLoading || !facilitySelected?.facilityId || !measurementSelected}
                onClick={handlePreviewReport}
                sx={{ py: 1.2, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
              >
                Preview
              </Button>
            </Box>
          </Stack>
        </Grid2>
      </Grid2>
    </Box>
  );
}